import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import { useAppAuth } from '../../hooks/useAppAuth';
import { useLastBuild } from '../../hooks/useLastBuild';
import { BuildAllModal } from '../BuildAllModal';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { describeLastBuildDetail, readBuildFreshness } from './buildStatus';

/**
 * The "<tenant> · <date> · data updated 2h ago" line under the greeting.
 *
 * The freshness half comes from the last BuildOrchestration on the branch the
 * caller is reading, so develop mode reports its own build rather than
 * production's. Per-source sync state is deliberately left to the "What Ellie
 * can see" panel — this line answers one question: how old is the data.
 *
 * A failed build is the one case that gets more than a clause: it becomes a
 * danger badge with a way into the build's logs — the same details modal the
 * DBT Builds settings tab opens, so there's one place to read a build.
 */
export default function SyncStatusLine() {
  const { user } = useAppAuth();
  const { data: lastBuild } = useLastBuild();
  const [logsOpen, setLogsOpen] = useState(false);

  const tenantName = user?.tenants?.find((t) => t.tenantId === user?.tenantId)?.name;
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const freshness = readBuildFreshness(lastBuild);
  const failed = freshness.kind === 'failed';

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
      <span title={describeLastBuildDetail(lastBuild)}>
        {[tenantName, today, failed ? null : freshness.note].filter(Boolean).join(' · ')}
      </span>

      {failed && (
        <>
          <Badge variant="destructive" title={freshness.detail ?? undefined}>
            {freshness.note}
          </Badge>
          {freshness.failedBuildId && (
            <>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-sm text-destructive"
                onClick={() => setLogsOpen(true)}
              >
                <ScrollText className="mr-1 h-3.5 w-3.5" />
                View Logs
              </Button>
              <BuildAllModal
                open={logsOpen}
                onOpenChange={setLogsOpen}
                orchestrationId={freshness.failedBuildId}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
